import React from "react";

import { Checkbox, Picto, Tooltip } from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";

import { useSelection } from "@/hooks/useSelection";

import { Blinker, Markdown, Tag } from "@/components/atoms";

import { cn } from "@/utils/cn";

import { TTask } from "@/types";

import "./TaskListItem.css";

export interface ITaskListItemProps {
	task: TTask;
	onClick?: (task: TTask) => void;
	onContextMenu?: (e: React.MouseEvent) => void;
	isActive?: boolean;
	hours?: boolean;
	selectable?: boolean;
	actions?: boolean;
}

const MAX_LINES = 4;
const SPLIT_REGEX = /\n{1,}/gi;

const TaskListItemInner: React.FC<ITaskListItemProps> = ({
	task,
	onClick,
	onContextMenu,
	isActive,
	hours = true,
	selectable = true,
	actions = true,
}) => {
	const { selected, isSelected, selectItem, selectMode } =
		useSelection<TTask>();

	const splittedHeight = task.workedOn?.trim()?.split(SPLIT_REGEX).length;

	const handleClick = () => {
		onClick?.(task);
	};

	const getText = () => {
		const lines = task.workedOn?.split(SPLIT_REGEX);
		return lines?.slice(0, MAX_LINES)?.join("\n");
	};

	const isFuture = new Date(task.startDate) > new Date();

	return (
		<div
			className={cn([
				"task-list-item group flex cursor-pointer items-center justify-between gap-4 rounded-lg p-2 hover:bg-neutral-100 md:px-4",
				isActive && "bg-primary-50!",
			])}
			onClick={handleClick}
			onContextMenu={onContextMenu}
			data-testid="task-list-item"
			data-task-id={task.id}
		>
			<div className="flex items-center gap-2 overflow-hidden">
				{!!selected.size || selectMode
					? selectable && (
							<div className="flex w-[1.2rem] items-center justify-center text-center">
								<Checkbox
									checked={isSelected(task)}
									onClick={(e) => {
										e.stopPropagation();
									}}
									onChange={() => {
										selectItem(task);
									}}
								/>
							</div>
						)
					: actions && (
							<button
								className="min-w-4 text-center"
								data-testid="task-list-item-menu"
								onClick={(e) => {
									e.stopPropagation();
									onContextMenu?.(e);
								}}
							>
								<Picto
									icon={"more"}
									className="h-5 w-5 rotate-90 text-neutral-500"
								/>
							</button>
						)}

				{hours && (
					<div className="flex h-full min-w-12 flex-col justify-between space-y-0 text-center text-neutral-400">
						<div>{format(task.startDate, "HH:mm")}</div>
						<div>
							{task.endDate ? format(task.endDate, "HH:mm") : "-"}
						</div>
					</div>
				)}

				<div
					className={cn([
						"relative overflow-hidden wrap-break-word",
						!hours && "pl-2",
					])}
				>
					<div className="row flex-wrap items-center gap-2">
						{!isFuture &&
							(!task.endDate ||
								(task.isMultiDay && !task.realEndDate)) && (
								<Tooltip content={t("tasks.taskInProgress")}>
									<Blinker className="bg-primary-500 m-1" />
								</Tooltip>
							)}
						<div className="font-bold">{task.name}</div>
					</div>
					<Markdown
						className="text-2xs [&_*]:break-word pointer-events-none overflow-hidden text-neutral-400 **:text-xs! [&_a]:break-all"
						components={{
							h1: "p",
							h2: "p",
							h3: "p",
							h4: "p",
						}}
					>
						{getText()}
					</Markdown>
					{splittedHeight > MAX_LINES && (
						<div
							className={cn([
								"absolute bottom-0 left-0 h-1/2 w-full bg-linear-to-t from-white to-transparent group-hover:from-neutral-100",
								isActive && "from-primary-50!",
							])}
						/>
					)}
				</div>
			</div>
			<div className="flex items-center justify-between gap-2">
				<div className="text-xs text-neutral-400">
					{!!task.notes && <div>Notes</div>}
				</div>
				<div className="">
					<Tag
						color={task.project?.color}
						className="max-w-[60px] overflow-hidden text-ellipsis max-sm:block"
					>
						{task.project?.name}
					</Tag>
				</div>
			</div>
		</div>
	);
};

export const TaskListItem = React.memo(TaskListItemInner);
